import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { bounds, createLayout, reheat, seed, settle, tick } from "./force";
import { buildChain } from "./trails";

// Above this many visible nodes the layout is solved in one go instead of
// being animated frame by frame.
const SETTLE_ABOVE = 120;

// The default cap on how many correspondences one thing shows at once.
const DEFAULT_PER_BRANCH = 12;
export const PER_BRANCH_CHOICES = [6, 12, 24, null];

// The picker's choice lives in the URL, like the Tree's lenses do.
function readKinds() {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("kinds");
  return raw ? raw.split(",").filter(Boolean) : null;
}

function writeKinds(kinds, present) {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  // All of them is the default and does not need saying.
  if (kinds.size === present.length) params.delete("kinds");
  else params.set("kinds", [...kinds].join(","));
  const query = params.toString();
  window.history.replaceState({}, "", query ? `${window.location.pathname}?${query}` : window.location.pathname);
}

const REDUCED = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

// The web, and what of it is currently showing.
//
// It starts at ONE node with its immediate neighbours, and grows outward as
// you open things. The whole graph is already in memory, so expanding is a
// lookup — there is no spinner anywhere in this interface.
export default function useAtlas({ focusKey = null, payload, start }) {
  const nodesByKey = useMemo(
    () => new Map(payload.nodes.map((node) => [node.key, node])),
    [payload.nodes]
  );
  // Edges arrive with node INDICES rather than keys — see
  // CorrespondenceAtlasPayload — so they are resolved once here and everything
  // downstream sees the `from`/`to` it always did.
  const edges = useMemo(
    () => payload.edges.map((edge) => ({
      ...edge,
      from: payload.nodes[edge.f]?.key,
      to: payload.nodes[edge.t]?.key
    })),
    [payload.edges, payload.nodes]
  );

  const edgesById = useMemo(
    () => new Map(edges.map((edge) => [edge.id, edge])),
    [edges]
  );

  // Derived rather than shipped: it is entirely implied by the edges, and
  // sending it was forty kilobytes of the same node keys written twice more.
  const adjacency = useMemo(() => {
    const map = {};
    edges.forEach((edge) => {
      (map[edge.from] ||= []).push(edge.id);
      (map[edge.to] ||= []).push(edge.id);
    });
    return map;
  }, [edges]);

  // Where the map begins. A prop at first, then STATE, because the setup
  // modal lets a reader choose somewhere else to start and the map has to be
  // able to move rather than only ever opening where the page put it.
  const [origin, setOrigin] = useState(() => start || payload.focus || payload.nodes[0]?.key);

  const [openKeys, setOpenKeys] = useState(() => new Set(origin ? [origin] : []));
  // How many of a thing's correspondences to draw before saying "and forty
  // more". Saturn has fifty-three and most of them are one of Agrippa's
  // plants; showing all of them at once is not showing anybody anything.
  const [perBranch, setPerBranchState] = useState(DEFAULT_PER_BRANCH);
  // Things the reader has asked to see in full anyway.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  // How much of the web to draw. "all" is the default, because a dense web is
  // sometimes the point and only the reader knows when it is in the way.
  //
  //   all       everything opened, and what it touches
  //   selected  the selection and what IT touches — not dimmed, gone
  //   chain     only the things picked, and the shortest way between them
  //
  // One control rather than two checkboxes, which would have fought over the
  // same question.
  const [showMode, setShowMode] = useState("all");
  const [chain, setChain] = useState([]);

  // How many things of each kind the whole web holds, for the picker.
  const kindCounts = useMemo(() => {
    const counts = {};
    payload.nodes.forEach((node) => { counts[node.group] = (counts[node.group] || 0) + 1; });
    return counts;
  }, [payload.nodes]);

  // Which kinds are IN the web. Everything, until a reader says otherwise —
  // and what they say goes in the URL, because a web narrowed to the metals
  // and the days is a thing somebody will want to send to someone.
  const [kinds, setKinds] = useState(() => {
    const wanted = readKinds();
    const present = Object.keys(kindCounts);
    return wanted ? new Set(wanted.filter((kind) => present.includes(kind))) : new Set(present);
  });

  useEffect(() => {
    writeKinds(kinds, Object.keys(kindCounts));
  }, [kinds, kindCounts]);
  const [version, setVersion] = useState(0);
  const layoutRef = useRef(null);
  const frameRef = useRef(null);

  if (layoutRef.current === null) layoutRef.current = createLayout(origin);

  // What an opened node actually shows: its strongest correspondences first,
  // capped, unless the reader has asked for the lot. Ranked server-side — see
  // Correspondences::Prominence, which orders by how load-bearing a
  // correspondence is and never by how true it is.
  const branchOf = useCallback((key) => {
    const all = (adjacency[key] || [])
      .map((id) => edgesById.get(id))
      .filter(Boolean)
      // The kind of the thing at the FAR end. Turning off "Countries and
      // provinces" is how a reader stops Saturn showing sixty-three of them.
      .filter((edge) => {
        const far = edge.from === key ? edge.to : edge.from;
        return kinds.has(nodesByKey.get(far)?.group);
      })
      .sort((a, b) => (b.weight || 0) - (a.weight || 0));
    const limit = expandedKeys.has(key) || perBranch === null ? all.length : perBranch;
    return { all, shown: all.slice(0, limit) };
  }, [adjacency, edgesById, expandedKeys, perBranch, kinds, nodesByKey]);

  // A node is DRAWN if it has been opened or is next to something opened. The
  // frontier is visible but closed, which is what gives the map its edge: you
  // can always see one step further than you have gone.
  // What the focus lands on: whatever is selected, or the thing the page is
  // about when nothing is.
  const focused = focusKey || origin;

  const chainResult = useMemo(
    () => buildChain(chain, { adjacency, edgesById }),
    [chain, adjacency, edgesById]
  );

  // Building a chain needs somewhere to go next, so the LAST thing picked
  // keeps its neighbours on the map. Without that, choosing "a chain I build"
  // drew nothing and there was nothing left to click.
  //
  // MEMOISED, and it has to be: a fresh array here is a fresh identity for
  // every memo that reads it, which re-ran the seeding effect, which reheated
  // the simulation, which re-rendered — a map that never settled and an
  // element Playwright could never call stable.
  const chainFrontier = useMemo(
    () => (chain.length > 0 ? branchOf(chain.at(-1)).shown : []),
    [chain, branchOf]
  );

  const visible = useMemo(() => {
    if (showMode === "chain" && chain.length > 0) {
      const keys = new Set(chainResult.nodes);
      chainFrontier.forEach((edge) => { keys.add(edge.from); keys.add(edge.to); });
      return keys;
    }

    const keys = new Set();
    // A chain with nothing picked yet starts from ONE thing, not from
    // everything opened so far. Falling through to the full view here is what
    // made choosing "a chain I build" look like it was showing the lot —
    // especially after "open the whole web", which had left five hundred
    // things open.
    const roots = (showMode === "selected" || showMode === "chain") && focused
      ? [focused]
      : openKeys;
    roots.forEach((key) => {
      keys.add(key);
      branchOf(key).shown.forEach((edge) => {
        keys.add(edge.from);
        keys.add(edge.to);
      });
    });
    return keys;
  }, [openKeys, branchOf, showMode, focused, chainResult, chain, chainFrontier]);

  // Only the edges an opened node actually shows. Filtering on "both ends are
  // visible" would quietly put the capped ones back, because the far end is
  // visible for a different reason.
  const visibleEdges = useMemo(() => {
    if (showMode === "chain" && chain.length > 0) {
      const shown = new Set(chainResult.edges);
      chainFrontier.forEach((edge) => shown.add(edge.id));
      return edges.filter((edge) => shown.has(edge.id));
    }

    const shown = new Set();
    const roots = (showMode === "selected" || showMode === "chain") && focused
      ? [focused]
      : openKeys;
    roots.forEach((key) => branchOf(key).shown.forEach((edge) => shown.add(edge.id)));
    return edges.filter((edge) => shown.has(edge.id));
  }, [edges, openKeys, branchOf, showMode, focused, chainResult, chain, chainFrontier]);

  const visibleKeys = useMemo(() => Array.from(visible), [visible]);

  // Seeded DURING the render, not in an effect: a node with no position draws
  // nothing, and seeding after the first paint meant the map was briefly — and
  // in a test environment permanently — empty. `seed` is idempotent and writes
  // to a ref, so this is a cache being filled, not state being set.
  visibleKeys.forEach((key) => {
    const layout = layoutRef.current;
    if (layout.positions.has(key)) return;
    const parentEdge = visibleEdges.find(
      (edge) => (edge.from === key && openKeys.has(edge.to)) ||
                (edge.to === key && openKeys.has(edge.from))
    );
    const parentKey = parentEdge
      ? (parentEdge.from === key ? parentEdge.to : parentEdge.from)
      : null;
    seed(layout, key, parentKey ? layout.positions.get(parentKey) : null);
  });

  // Then let the simulation push the new arrivals out to where they belong.
  useEffect(() => {
    const layout = layoutRef.current;

    // Animating is what makes an expansion read as growth, but every tick
    // re-renders every node and edge, and "open the whole web" is now three
    // hundred of each. Past a certain size the honest thing is to arrive
    // already settled rather than to stutter getting there.
    if (REDUCED() || visibleKeys.length > SETTLE_ABOVE) {
      settle(layout, visibleKeys, visibleEdges);
      setVersion((value) => value + 1);
      return undefined;
    }

    reheat(layout);
    const run = () => {
      const moving = tick(layout, visibleKeys, visibleEdges);
      setVersion((value) => value + 1);
      frameRef.current = moving ? requestAnimationFrame(run) : null;
    };
    if (frameRef.current === null) frameRef.current = requestAnimationFrame(run);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [visibleKeys, visibleEdges, openKeys]);

  const open = useCallback((key) => {
    setOpenKeys((current) => {
      if (current.has(key)) return current;
      const next = new Set(current);
      next.add(key);
      return next;
    });
  }, []);

  const collapse = useCallback((key) => {
    setOpenKeys((current) => {
      if (!current.has(key) || current.size === 1) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }, []);

  // Changing the cap clears what was individually expanded, or a reader who
  // opened a few "+41 more" and then asked for "strongest 6" would still be
  // looking at forty-one of them. The control has to mean what it says
  // everywhere, not everywhere except the places already overridden.
  const setPerBranch = useCallback((value) => {
    setPerBranchState(value);
    setExpandedKeys(new Set());
  }, []);

  // "…and forty more" for one thing.
  const expand = useCallback((key) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      next.add(key);
      return next;
    });
  }, []);

  // How many this node is holding back.
  const hiddenAround = useCallback((key) => {
    const { all, shown } = branchOf(key);
    return all.length - shown.length;
  }, [branchOf]);

  // Picking a thing while building a chain ADDS it. Picking the same thing
  // twice in a row is a slip, not a loop, and is ignored.
  const addToChain = useCallback((key) => {
    setChain((current) => (current.at(-1) === key ? current : [...current, key]));
  }, []);

  const dropFromChain = useCallback((at) => {
    setChain((current) => current.filter((_, index) => index !== at));
  }, []);

  const clearChain = useCallback(() => setChain([]), []);

  // Start somewhere else: open it, make it the thing the map is about, and
  // drop everything that was open around the old start — otherwise choosing a
  // new beginning would land the reader in the middle of their old one.
  const goTo = useCallback((key) => {
    if (!key) return;
    setOrigin(key);
    setOpenKeys(new Set([key]));
    setExpandedKeys(new Set());
    setChain([]);
  }, []);

  // Opens every thing. It does NOT touch the cap: it used to set it to "all
  // of them" and never put it back, so one press of this permanently undid a
  // reader's "strongest 12" and the map went on showing everything with the
  // control still claiming otherwise. Opening is about WHICH things are on the
  // map; the cap is about how much of each — two different questions.
  const openAll = useCallback(() => {
    setOpenKeys(new Set(payload.nodes.map((node) => node.key)));
  }, [payload.nodes]);

  const reset = useCallback(() => {
    setOpenKeys(new Set(origin ? [origin] : []));
    setExpandedKeys(new Set());
  }, [origin]);

  // Every edge one node is an end of, whichever end that is.
  const edgesFor = useCallback(
    (key) => (adjacency[key] || []).map((id) => edgesById.get(id)).filter(Boolean),
    [adjacency, edgesById]
  );

  const positions = layoutRef.current.positions;
  const frame = bounds(layoutRef.current, visibleKeys);

  return {
    adjacency,
    addToChain,
    goTo,
    branchOf,
    chain,
    chainResult,
    clearChain,
    dropFromChain,
    focused,
    setShowMode,
    showMode,
    kindCounts,
    kinds,
    setKinds,
    collapse,
    edgesById,
    edgesFor,
    expand,
    expandedKeys,
    hiddenAround,
    perBranch,
    setPerBranch,
    frame,
    nodesByKey,
    open,
    openAll,
    openKeys,
    origin,
    positions,
    reset,
    version,
    visibleEdges,
    visibleKeys,
    // How much of the web is still out there, which is the invitation.
    unopened: payload.nodes.length - openKeys.size
  };
}
